/**
 * Component — Especialização de BaseUiNode para componentes internos da nova área.
 *
 * Diferenças em relação a Page:
 *   - exige root ou selector explícito (nunca faz fallback para document.body)
 *   - nunca faz auto-mount no DOMContentLoaded
 *   - mount() deve ser chamado manualmente pelo consumidor
 *   - funciona apenas dentro do escopo do seu root
 *
 * Uso:
 *   class MeuComponente extends Component {
 *       onInit()        { ... }
 *       bindEvents()    { ... }
 *       afterViewInit() { ... }
 *   }
 *
 *   // Instanciar e montar manualmente:
 *   const comp = new MeuComponente({
 *       componentId: 'meu-componente',
 *       root: document.getElementById('meu-container'),
 *   });
 *   comp.mount();
 */
import BaseUiNode from './base-ui-node';

class Component extends BaseUiNode {

    /**
     * @param {Object} config
     * @param {Element}  [config.root]      Elemento root explícito.
     * @param {string}   [config.selector]  Seletor CSS para resolver o root.
     * @param {string}   [config.componentId]  ID lógico do componente.
     */
    constructor(config = {}) {
        if (!config.componentId) {
            config.componentId = BaseUiNode.uid('comp');
        }
        super(config);
    }

    // ─── Root resolve: Component EXIGE root ou selector ───────────────

    /**
     * Resolve o root element. Diferente de Page, Component nunca usa
     * document.body como fallback silencioso — lança erro se não
     * encontrar um elemento válido.
     *
     * @param {Object} config
     * @returns {Element}
     * @throws {Error} Se nenhum root ou selector válido for fornecido.
     */
    _resolveRoot(config) {
        if (config.root instanceof Element) return config.root;

        if (config.selector) {
            const el = document.querySelector(config.selector);
            if (el) return el;
        }

        throw new Error(
            `[Component] "${config.componentId ?? 'unknown'}": root ou selector válido é obrigatório. ` +
            `Forneça config.root (Element) ou config.selector (CSS selector que resolva para um elemento existente).`
        );
    }
}

export default Component;
